/**
 * Mobile Spacing Utilities for PaymentReceiptsModal
 * 
 * This module provides responsive spacing utilities and hooks for mobile optimization.
 * It implements the mobile spacing requirements defined in the design specification.
 */

import { useTheme, useMediaQuery } from '@mui/material';

/**
 * Custom hook for responsive breakpoint detection
 * @returns {Object} Object containing breakpoint detection flags
 */
export const useResponsiveBreakpoints = () => {
  const theme = useTheme();
  
  return {
    isMobile: useMediaQuery(theme.breakpoints.down('sm')),
    isTablet: useMediaQuery(theme.breakpoints.between('sm', 'md')),
    isDesktop: useMediaQuery(theme.breakpoints.up('md')),
    isSmallMobile: useMediaQuery('(max-width:375px)'),
    isLargeMobile: useMediaQuery(theme.breakpoints.between('xs', 'sm'))
  };
};

/**
 * Mobile spacing configuration constants
 * Implements Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 5.1, 5.2, 5.3, 5.4, 5.5
 */
export const MOBILE_SPACING_CONFIG = {
  // Modal container spacing (Requirement 1.1, 1.3)
  container: {
    padding: {
      mobile: 1,      // 1rem instead of 2-3rem
      desktop: 3
    },
    margin: {
      mobile: 1,      // 1rem instead of 2-3rem  
      desktop: 2
    }
  },
  
  // Form section spacing (Requirement 1.2, 5.1)
  section: {
    padding: {
      mobile: 1.5,    // 1.5rem instead of 2.5-3.5rem
      desktop: 3
    },
    marginBottom: {
      mobile: 2,      // 2rem instead of 3rem
      desktop: 3
    }
  },
  
  // Grid spacing (Requirement 1.4)
  grid: {
    spacing: {
      mobile: 2,      // spacing of 2 instead of 3
      desktop: 3
    }
  },
  
  // Modal header spacing (Requirement 1.5)
  header: {
    verticalPadding: {
      mobile: 1.5,    // 1.5rem instead of 2rem
      desktop: 2
    }
  },
  
  // Element gaps and spacing (Requirements 5.2, 5.3, 5.4, 5.5)
  gaps: {
    horizontal: {
      mobile: 1.5,    // 1.5rem instead of 2rem
      desktop: 2
    },
    buttonArea: {
      mobile: 1.5,    // 1.5rem instead of 2rem
      desktop: 2
    },
    dividerMargin: {
      mobile: 2,      // 2rem instead of 3rem
      desktop: 3
    },
    iconGaps: {
      mobile: 1,      // 1rem instead of 1.5-2rem
      desktop: 1.5
    }
  }
};

/**
 * Form field configuration for mobile optimization
 * Implements Requirements 2.1, 2.2, 2.3, 2.4, 2.5
 */
export const MOBILE_FIELD_CONFIG = {
  // Field dimensions (Requirement 2.1, 2.4)
  dimensions: {
    height: {
      mobile: 40,     // 40px instead of 48px
      desktop: 48
    },
    padding: {
      mobile: 0.75,   // 0.75rem instead of 1rem
      desktop: 1
    }
  },
  
  // Typography scaling (Requirements 2.2, 2.3)
  typography: {
    label: {
      mobile: '0.8rem',   // 0.8rem instead of 0.9rem
      desktop: '0.9rem'
    },
    input: {
      mobile: '0.85rem',  // 0.85rem instead of 0.9rem
      desktop: '0.9rem'
    },
    helper: {
      mobile: '0.7rem',   // 0.7rem instead of 0.75rem
      desktop: '0.75rem'
    }
  },
  
  // Textarea configuration (Requirement 2.5)
  textarea: {
    rows: {
      mobile: 3,      // 3 rows instead of 4
      desktop: 4
    }
  }
};

/**
 * Table component mobile configuration
 * Implements Requirements 3.1, 3.2, 3.3, 3.4, 3.5
 */
export const MOBILE_TABLE_CONFIG = {
  // Container settings (Requirement 3.5)
  container: {
    maxHeight: {
      mobile: 300,    // 300px instead of 400px
      desktop: 400
    }
  },
  
  // Cell and header padding (Requirements 3.1, 3.2)
  padding: {
    header: {
      mobile: '12px', // 12px instead of 16px
      desktop: '16px'
    },
    cell: {
      mobile: '10px', // 10px instead of 16px
      desktop: '16px'
    }
  },
  
  // Typography (Requirement 3.3)
  typography: {
    fontSize: {
      mobile: '0.75rem', // 0.75rem instead of 0.85rem
      desktop: '0.85rem'
    }
  },
  
  // Input fields in table (Requirement 3.4)
  input: {
    height: {
      mobile: 32,     // 32px instead of 36px
      desktop: 36
    }
  }
};

/**
 * Typography scaling configuration for mobile
 * Implements Requirements 4.1, 4.2, 4.3, 4.4, 4.5
 */
export const MOBILE_TYPOGRAPHY_CONFIG = {
  // Section headers (Requirement 4.1)
  sectionTitle: {
    mobile: '0.85rem',  // 0.85rem instead of 0.95rem
    desktop: '0.95rem'
  },
  
  // Modal title (Requirement 4.2)
  modalTitle: {
    mobile: '1rem',     // 1rem instead of 1.1-1.25rem
    desktop: '1.25rem'
  },
  
  // Helper text (Requirement 4.3)
  helperText: {
    mobile: '0.7rem',   // 0.7rem instead of 0.75rem
    desktop: '0.75rem'
  },
  
  // Button labels (Requirement 4.4)
  buttonLabel: {
    mobile: '0.85rem',  // 0.85rem instead of 0.9rem
    desktop: '0.9rem'
  },
  
  // Chip labels (Requirement 4.5)
  chipLabel: {
    mobile: '0.7rem',   // 0.7rem instead of 0.75rem
    desktop: '0.75rem'
  }
};

/**
 * Touch target configuration for accessibility compliance
 * Implements Requirements 6.1, 6.2, 6.3, 6.4, 6.5
 */
export const TOUCH_TARGET_CONFIG = {
  // Minimum touch target size (WCAG 2.1 AA compliance)
  minSize: 44, // 44px minimum for all interactive elements
  
  // Button configuration (Requirement 6.1)
  button: {
    minHeight: 44,
    minWidth: 44
  },
  
  // Checkbox configuration (Requirements 6.2, 6.4)
  checkbox: {
    padding: 12,        // Creates 44px touch area
    iconSize: '1.4rem'
  },
  
  // Select dropdown configuration (Requirement 6.3)
  select: {
    minHeight: 44
  },
  
  // Close button configuration (Requirement 6.5)
  closeButton: {
    minSize: 44
  }
};

/**
 * Performance optimization configuration for mobile
 * Implements Requirements 8.1, 8.2, 8.3, 8.4, 8.5
 */
export const MOBILE_PERFORMANCE_CONFIG = {
  // Animation settings (Requirements 8.1, 8.4)
  animations: {
    transitionDuration: {
      mobile: '200ms',    // 200ms instead of 300ms
      desktop: '300ms'
    },
    reducedMotion: true   // Support for prefers-reduced-motion
  },
  
  // Visual effects (Requirements 8.2, 8.5)
  effects: {
    backdropBlur: {
      mobile: 'blur(4px)',  // Reduced blur for performance
      desktop: 'blur(8px)'
    },
    simplifiedLoading: true // Simplified loading animations
  },
  
  // Interaction settings (Requirement 8.3)
  interactions: {
    disableHoverOnMobile: true // Disable hover states on mobile
  }
};

/**
 * Utility function to get responsive spacing values
 * @param {string} category - The spacing category (container, section, etc.)
 * @param {string} property - The specific property (padding, margin, etc.)
 * @param {boolean} isMobile - Whether the current viewport is mobile
 * @returns {number|string} The appropriate spacing value
 */
export const getResponsiveSpacing = (category, property, isMobile) => {
  const config = MOBILE_SPACING_CONFIG[category];
  if (!config || !config[property]) {
    console.warn(`Invalid spacing configuration: ${category}.${property}`);
    return 0;
  }
  
  return isMobile ? config[property].mobile : config[property].desktop;
};

/**
 * Utility function to get responsive field configuration
 * @param {string} property - The field property (height, padding, etc.)
 * @param {boolean} isMobile - Whether the current viewport is mobile
 * @returns {number|string} The appropriate field value
 */
export const getResponsiveField = (property, isMobile) => {
  const config = MOBILE_FIELD_CONFIG.dimensions[property] || MOBILE_FIELD_CONFIG.typography[property];
  if (!config) {
    console.warn(`Invalid field configuration: ${property}`);
    return isMobile ? 40 : 48; // Default fallback
  }
  
  return isMobile ? config.mobile : config.desktop;
};

/**
 * Utility function to get responsive table configuration
 * @param {string} category - The table category (container, padding, etc.)
 * @param {string} property - The specific property
 * @param {boolean} isMobile - Whether the current viewport is mobile
 * @returns {number|string} The appropriate table value
 */
export const getResponsiveTable = (category, property, isMobile) => {
  const config = MOBILE_TABLE_CONFIG[category];
  if (!config || !config[property]) {
    console.warn(`Invalid table configuration: ${category}.${property}`);
    return isMobile ? 300 : 400; // Default fallback
  }
  
  return isMobile ? config[property].mobile : config[property].desktop;
};

/**
 * Utility function to get responsive typography
 * @param {string} type - The typography type (sectionTitle, modalTitle, etc.)
 * @param {boolean} isMobile - Whether the current viewport is mobile
 * @returns {string} The appropriate font size
 */
export const getResponsiveTypography = (type, isMobile) => {
  const config = MOBILE_TYPOGRAPHY_CONFIG[type];
  if (!config) {
    console.warn(`Invalid typography configuration: ${type}`);
    return isMobile ? '0.85rem' : '0.9rem'; // Default fallback
  }
  
  return isMobile ? config.mobile : config.desktop;
};

/**
 * Utility function to create responsive sx props for Material-UI components
 * @param {boolean} isMobile - Whether the current viewport is mobile
 * @param {Object} mobileStyles - Styles to apply on mobile
 * @param {Object} desktopStyles - Styles to apply on desktop
 * @returns {Object} Material-UI sx prop object
 */
export const createResponsiveSx = (isMobile, mobileStyles = {}, desktopStyles = {}) => {
  return isMobile ? mobileStyles : desktopStyles;
};

/**
 * Hook to get all mobile spacing utilities for a component
 * @returns {Object} Complete mobile spacing configuration
 */
export const useMobileSpacing = () => {
  const { isMobile, isTablet, isDesktop, isSmallMobile } = useResponsiveBreakpoints();
  
  return {
    // Breakpoint flags
    isMobile,
    isTablet, 
    isDesktop,
    isSmallMobile,
    
    // Spacing utilities
    container: {
      padding: getResponsiveSpacing('container', 'padding', isMobile),
      margin: getResponsiveSpacing('container', 'margin', isMobile)
    },
    
    section: {
      padding: getResponsiveSpacing('section', 'padding', isMobile),
      marginBottom: getResponsiveSpacing('section', 'marginBottom', isMobile)
    },
    
    grid: {
      spacing: getResponsiveSpacing('grid', 'spacing', isMobile)
    },
    
    header: {
      verticalPadding: getResponsiveSpacing('header', 'verticalPadding', isMobile)
    },
    
    gaps: {
      horizontal: getResponsiveSpacing('gaps', 'horizontal', isMobile),
      buttonArea: getResponsiveSpacing('gaps', 'buttonArea', isMobile),
      dividerMargin: getResponsiveSpacing('gaps', 'dividerMargin', isMobile),
      iconGaps: getResponsiveSpacing('gaps', 'iconGaps', isMobile)
    },
    
    // Field configuration
    field: {
      height: getResponsiveField('height', isMobile),
      padding: getResponsiveField('padding', isMobile)
    },
    
    // Typography
    typography: {
      title: getResponsiveTypography('modalTitle', isMobile),
      label: getResponsiveField('label', isMobile),
      input: getResponsiveField('input', isMobile),
      helper: getResponsiveField('helper', isMobile),
      section: getResponsiveTypography('sectionTitle', isMobile),
      button: getResponsiveTypography('buttonLabel', isMobile),
      chip: getResponsiveTypography('chipLabel', isMobile)
    },
    
    // Table configuration
    table: {
      maxHeight: getResponsiveTable('container', 'maxHeight', isMobile),
      cellPadding: getResponsiveTable('padding', 'cell', isMobile),
      headerPadding: getResponsiveTable('padding', 'header', isMobile),
      fontSize: getResponsiveTable('typography', 'fontSize', isMobile),
      inputHeight: getResponsiveTable('input', 'height', isMobile)
    },
    
    // Touch targets
    touchTarget: TOUCH_TARGET_CONFIG,
    
    // Performance settings
    performance: {
      transitionDuration: isMobile ? 
        MOBILE_PERFORMANCE_CONFIG.animations.transitionDuration.mobile : 
        MOBILE_PERFORMANCE_CONFIG.animations.transitionDuration.desktop,
      backdropBlur: isMobile ?
        MOBILE_PERFORMANCE_CONFIG.effects.backdropBlur.mobile :
        MOBILE_PERFORMANCE_CONFIG.effects.backdropBlur.desktop,
      disableHover: isMobile && MOBILE_PERFORMANCE_CONFIG.interactions.disableHoverOnMobile
    },
    
    // Utility functions
    createResponsiveSx: (mobileStyles, desktopStyles) => 
      createResponsiveSx(isMobile, mobileStyles, desktopStyles)
  };
};

export default {
  useResponsiveBreakpoints,
  useMobileSpacing,
  MOBILE_SPACING_CONFIG,
  MOBILE_FIELD_CONFIG,
  MOBILE_TABLE_CONFIG,
  MOBILE_TYPOGRAPHY_CONFIG,
  TOUCH_TARGET_CONFIG,
  MOBILE_PERFORMANCE_CONFIG,
  getResponsiveSpacing,
  getResponsiveField,
  getResponsiveTable,
  getResponsiveTypography,
  createResponsiveSx
};